<template>
  <div
    class="browser-list-item w-full flex items-center"
    :class="{ 'bg-primary5': isSelected, '!bg-primary4': isContextMenuOpen || isDraggedOver }"
  >
    <div class="flex items-center">
      <NCheckbox
        :checked="selectedItemIds.includes(item.id as string)"
        class="h-9 w-9 inline-flex items-center justify-center cursor-pointer mx-[5px]"
        :value="item.id"
        @click.stop="
          toggleItemSelection(getTypedItem(item), {
            disableMultiple: !launchDarkly.allow_bulk_actions_canvases_files,
          })
        "
      />

      <div class="my-3">
        <CFileThumbnail :item="item as RenderableFile" />
      </div>
    </div>
    <div class="flex flex-col justify-center gap-1 pa-3 truncate w-full">
      <CTableInput
        :modelValue="isEditingCurrentItem(item?.id, 'name') ? nameForEditing : nameDescription.name"
        :readonly="isReadonly()"
        :search="highlightText"
        :state="isEditingCurrentItem(item?.id, 'name') ? 'edit' : 'view'"
        valueClass="font-700 font-size-[18px] whitespace-normal break-words overflow-visible"
        @cancel="clearInlineEditingItem()"
        @edit="onEdit(item.id, 'name')"
        @update:modelValue="onUpdateName(item.id.toString(), item.type, $event)"
      />
      <div
        v-if="isFolderNameDescriptionEnabled && item.type === 'folder' && nameDescription.description"
        class="font-size-[14px] tracking-[0.4px] leading-normal color-text2 whitespace-pre-wrap"
      >
        {{ nameDescription.description }}
      </div>
      <div
        class="truncate font-size-[12px]"
        :style="{ color: isContextMenuOpen || isDraggedOver ? themeVars.text2 : themeVars.text3 }"
      >
        <span v-if="item.type === 'folder'">
          <CIcon class="mr-1" icon="folder" />
          <span v-if="doNotShowFolderDetails">{{ t('canvasUI.common.folder') }}</span>
          <span v-else>{{ getFolderDescription(getItemAsFolder(item)) }}</span>
        </span>

        <div v-else-if="shouldShowFolderInfo" class="flex gap-2 items-center">
          <div class="flex gap-1 items-center">
            <div>{{ t('canvasUI.files.in') }}</div>
            <div class="color-text3 font-bold hover:underline" @click.stop="openFolder(item.folder.id)">
              {{ item.folder?.name?.replace('root', t('canvasUI.browserApp.content.breadcrumbs.root')) }}
            </div>
            <span class="inline-block h-1 w-1 bg-text3 rounded-full"></span>
            <component :is="renderContentType(item.content_type)" />
          </div>
        </div>

        <component :is="renderContentType(item.content_type)" v-else />
      </div>
      <div class="flex flex-wrap gap-2 items-center">
        <CDynamicIndicator
          v-for="metadataProperty of pitcherSettings.metadata_highlight_properties ?? []"
          :key="metadataProperty.key"
          :item="item"
          :metadataProperty="metadataProperty"
        />
      </div>
      <CTableTag
        v-if="item?.tags"
        display-as-text
        :maxTagsToShow="0"
        :modelValue="item?.tags"
        :readonly="isReadonly() || !!item.page"
        :search="highlightText"
        :state="isEditingCurrentItem(item?.id, 'tags') ? 'edit' : 'view'"
        :suggested-tags-fetcher="(search) => fetchSuggestedInstanceTags(search, { type: ['files'] })"
        :tagsClass="'cursor-pointer'"
        @cancel="clearInlineEditingItem()"
        @edit="onEdit(item.id, 'tags')"
        @update:modelValue="onUpdateTags(item.id.toString(), $event)"
      />
      <div
        v-if="
          pitcherSettings.enable_metadata_info_in_list_item && item.type === 'file' && Object.keys(metadata).length > 0
        "
        class="flex flex-wrap gap-2 mb-1"
        :class="{
          'mt-1': item?.tags?.length,
        }"
      >
        <CMetaDataBadge
          v-for="(meta, key) in metadataForListItem"
          :key="key"
          :highlight-text="highlightText"
          :label="meta.label"
          :value="meta.value"
        />
      </div>
      <div class="flex flex-wrap gap-2">
        <template v-if="isAdmin">
          <CInfoBadge
            v-if="isFailedFile"
            v-tooltip="t('canvasUI.files.failedTooltip')"
            :background-color="themeVars.error2"
            :color="themeVars.error"
            icon="file-slash"
            :text="t('canvasUI.files.conversionFailed')"
          />
          <CContentError v-if="item?.access_type === 'restricted'" only-tag restricted />
          <CInfoBadge
            v-if="isExpired"
            v-tooltip="
              t('canvasUI.files.expiredTooltip', {
                time: dayjsInstance(expiresAt).format('HH:mm'),
                date: dayjsInstance(expiresAt).format('LL'),
              })
            "
            :background-color="themeVars.error2"
            :color="themeVars.error"
            fa-type="fa-kit"
            icon="regular-clock-circle-exclamation"
            :text="t('canvasUI.files.expired')"
          />
          <CInfoBadge
            v-if="isExpiring && !isExpired"
            v-tooltip="
              t('canvasUI.files.expiringTooltip', {
                time: dayjsInstance(expiresAt).format('HH:mm'),
                date: dayjsInstance(expiresAt).format('LL'),
              })
            "
            :background-color="themeVars.warning2"
            :color="themeVars.warning3"
            fa-type="fa-kit"
            icon="regular-clock-circle-exclamation"
            :text="`${t('canvasUI.files.expiring')} ${isExpiring}`"
          />
        </template>
        <CInfoBadge
          v-if="item?.permissions?.can_share === false"
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="solid-share-from-square-slash"
          :text="t('canvasUI.files.notShareable')"
        />
        <CPopularityAndEngagementExtended
          v-if="Object.keys(popularityDict)?.length"
          class="gap-2 mr-2"
          :engagement="launchDarkly.show_popularity_icons ? popularityDict[item.id]?.engagement : undefined"
          :is-recommended="!!recommendedSectionIds.includes(item.id?.toString())"
          :popularity="launchDarkly.show_popularity_icons ? popularityDict[item.id]?.popularity : undefined"
          :views="launchDarkly.show_popularity_icons ? (popularityDict[item.id]?.views ?? 0) : 0"
        />
      </div>
    </div>

    <div class="flex justify-end h-full items-center gap-1 cursor-auto py-3 px-4" @click.stop>
      <slot name="actions"></slot>
    </div>
  </div>
</template>
<script setup lang="ts">
import { NCheckbox } from 'naive-ui'
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CDynamicIndicator from '@lib/components/CDynamicIndicator/CDynamicIndicator.vue'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import CMetaDataBadge from '@lib/components/CMetaDataBadge/CMetaDataBadge.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'

import useApiStore from '@browser/stores/api'
import useAppStore, { type StateSelectedItem } from '@browser/stores/app'
import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import useThemeVars from '@lib/composables/useThemeVars'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'
import dayjsInstance from 'dayjs'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { RenderableFile, ViewCompactFolder } from '@lib/types/files'
import { type OrganizationSettings } from '@lib/types/organizationSettings.types'
import { type InstanceSettings } from '@lib/types/instanceSettings.types'

import { openFolder } from '@browser/util/file'
import CContentError from '@lib/components/CContentError/CContentError.vue'

const props = withDefaults(
  defineProps<{
    filters: ComputedRef<Record<string, any>>
    highlightText?: string
    doNotShowFolderDetails?: boolean
    isContextMenuOpen?: boolean
    isDraggedOver?: boolean
    isSelected?: boolean
    item: { [key: string]: any; id: number | string }
    metadata?: Record<string, any>
  }>(),
  {
    highlightText: '',
    doNotShowFolderDetails: false,
    isContextMenuOpen: false,
    isDraggedOver: false,
    isSelected: false,
    metadata: () => ({}),
  },
)

const { t } = useI18n()
const { state: apiState } = useApiStore()
const {
  appName,
  isAdmin,
  clearInlineEditingItem,
  inlineEditingItem,
  selectedItemIds,
  toggleItemSelection,
  showFilteredResults,
} = useAppStore()
const { getFolderDescription, renderContentType, isFileExpired, isFileExpiring } = useFileDisplayHelpers()
const { popularityDict } = useSectionPopularity(props.filters)
const { recommendedSectionIds } = useRecommendations()
const { fetchSuggestedInstanceTags } = useSuggestedTags()
const themeVars = useThemeVars()
const { createNameDescriptionForItem, isFolderNameDescriptionEnabled } = useFolderNameDescription()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const expiresAt = computed(() => props.item.expires_at || props.item.file?.expires_at)

const isExpired = computed(() => isFileExpired(expiresAt.value))

const isExpiring = computed(() => isFileExpiring(expiresAt.value))

const isFailedFile = computed(() => {
  return props.item.status === 'failed' || props.item.file?.status === 'failed'
})

const nameDescription = createNameDescriptionForItem(
  computed(() => props.item.name ?? ''),
  computed(() => props.item.type ?? ''),
)

const nameForEditing = computed(() => {
  // When editing, always show the full name including | and description
  return props.item.name ?? ''
})

const metadataForListItem = computed(() => {
  const skipKeys = pitcherSettings.value?.skip_metadata_keys_in_list_item || []
  return Object.fromEntries(Object.entries(props.metadata).filter(([key]) => !skipKeys.includes(key)))
})

const shouldShowFolderInfo = computed(() => showFilteredResults.value && !!props.item.folder?.name)

function getItemAsFolder(item: any) {
  return item as ViewCompactFolder
}

function getTypedItem(item: any) {
  return item as StateSelectedItem
}

function isEditingCurrentItem(id: string | number, prop: 'name' | 'tags') {
  return inlineEditingItem.value?.id === id && inlineEditingItem.value?.prop === prop
}

function isReadonly() {
  return appName.value === 'impact' || !!inlineEditingItem.value?.id
}

function onEdit(id: string | number, prop: 'name' | 'tags') {
  inlineEditingItem.value = { id, prop }
}

async function onUpdateName(id: string, type: 'file' | 'folder', newValue: string) {
  switch (type) {
    case 'folder':
      await apiState.folderUpdater?.({ id, name: newValue })
      break
    case 'file':
      await apiState.fileUpdater?.({ id, name: newValue })
      break
  }
  clearInlineEditingItem()
}

async function onUpdateTags(id: string, newValue: string[]) {
  await apiState.fileUpdater?.({ id, tags: newValue })
  clearInlineEditingItem()
}
</script>

<style scoped lang="scss">
.browser-list-item {
  border-bottom: 1px solid v-bind('themeVars.primary6');
}
</style>
